import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { FormInputDate } from '@/components/forms/form-input-date.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { groupStudentsAndCreateRegularSessions } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { InstantCompare, instantIntervalsFromClassTime, MultiMap } from '@airreading/common';
import { StudentOfSchool } from '@airreading/common/data';
import { ReactNode, useMemo } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { CoachAssignedProps } from '../$schoolId/students.js';

export interface GenerateRegularSessionsProps {
  students: StudentOfSchool[];
  orgId: string;
  trigger: ReactNode;
  onSuccess?: () => void;
  onCoachesAssigned?: (props: CoachAssignedProps) => void;
  schoolTimeZone: string | null;
}

const maxGroupSize = 3;
const formSchema = z.object({
  groupSize: z.coerce
    .number()
    .int()
    .min(1, { message: 'Group size must be at least 1' })
    .max(maxGroupSize, {
      message: `Group size must be at most ${maxGroupSize}`,
    }),
  autoCreateSessions: z.boolean(),
  prioritizeTeachersWithoutClasses: z.boolean(),
  // Used to pick an override when the schedule start date is in the past
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
});

export const GenerateRegularSessions = ({
  students,
  orgId,
  trigger,
  onSuccess,
  onCoachesAssigned,
  schoolTimeZone,
}: GenerateRegularSessionsProps) => {
  const classTimes = MultiMap.groupBy(students, (s) => s.requestedClassTime?.id || 'empty');
  const studentsWithInvalidStartingLessons = students.filter((s) => !s.learningPath || !s.startingLesson);
  const invalidSchedule = classTimes.size > 1 || classTimes.has('empty');

  // calculate whether to show the date selection (logic in AIR-1825)
  const { isScheduleStartInPast, nextSessionPlainDate } = useMemo(() => {
    if (invalidSchedule || !schoolTimeZone) {
      return {
        isScheduleStartInPast: false,
        nextSessionPlainDate: undefined,
      };
    }

    const classTime = students?.[0]?.requestedClassTime;
    if (!classTime) {
      return {
        isScheduleStartInPast: false,
        nextSessionPlainDate: undefined,
      };
    }

    const intervals = instantIntervalsFromClassTime({ classTime });
    const now = Temporal.Now.instant();

    const firstInterval = intervals.next();
    if (firstInterval.done) {
      return {
        isScheduleStartInPast: false,
        nextSessionPlainDate: undefined,
      };
    }

    const firstClassStart = firstInterval.value.start;
    const isInPast = InstantCompare(firstClassStart).isBefore(now);
    if (!isInPast) {
      return {
        isScheduleStartInPast: false,
        nextSessionPlainDate: undefined,
      };
    }

    // Find the first session starting on or after now
    let nextPlainDate: Temporal.PlainDate | undefined;
    for (const interval of intervals) {
      if (InstantCompare(interval.start).isAfterOrEqual(now)) {
        nextPlainDate = interval.start.toZonedDateTimeISO(schoolTimeZone).toPlainDate();
        break;
      }
    }

    return {
      isScheduleStartInPast: true,
      nextSessionPlainDate: nextPlainDate,
    };
  }, [students, invalidSchedule, schoolTimeZone]);

  const defaultValues: DefaultValues<z.infer<typeof formSchema>> = {
    groupSize: maxGroupSize,
    autoCreateSessions: false,
    prioritizeTeachersWithoutClasses: false,
    overrideStartPlainDate: nextSessionPlainDate,
  };
  const { toast } = useToast();

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { groupSize, autoCreateSessions, prioritizeTeachersWithoutClasses, overrideStartPlainDate } = formData;
    const classTimeId = classTimes.keys().next().value;
    if (!classTimeId) {
      toast({
        title: 'Error generating regular sessions',
        description: 'All selected students must have the same class time.',
        variant: 'destructive',
      });
      return;
    }

    return groupStudentsAndCreateRegularSessions({
      studentIds: students.map((s) => s.id),
      classTimeId,
      groupSize,
      autoCreateSessions,
      prioritizeTeachersWithoutClasses,
      orgId,
      overrideStartPlainDate,
    })
      .then(
        ({
          studentGroupsCount,
          sessionsCount,
          availableCoachesCount,
          coachesAssignedCount,
          sessionGenerationWarnings,
        }) => {
          toast({
            title: `Generated regular sessions for ${studentGroupsCount} student groups and ${sessionsCount} sessions`,
          });
          for (const warning of sessionGenerationWarnings ?? []) {
            toast({
              title: warning,
              variant: 'destructive',
            });
          }
          onSuccess?.();
          closeModal();

          if (studentGroupsCount > 0) {
            onCoachesAssigned?.({ availableCoachesCount, coachesAssignedCount });
          }
        },
      )
      .catch((e) => {
        toast({
          title: 'Error generating regular sessions',
          description: airServerErrorFrom(e)?.message,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      formSchema={formSchema}
      trigger={trigger}
      modalTitle="Generate Regular Classes"
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      dialogContentProps={{ className: 'lg:max-w-screen-xs' }}
    >
      {({ form }) => {
        if (studentsWithInvalidStartingLessons.length > 0) {
          return (
            <div className="mt-4">
              All selected students must have the assigned starting lesson and learning path. The students below do not
              have a starting lesson or learning path assigned
              <ul className="list-disc pl-6">
                {studentsWithInvalidStartingLessons.map((student) => (
                  <li key={student.id}>
                    {student.firstName} {student.lastName}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        if (invalidSchedule) {
          return (
            <div className="mt-4">
              All selected students must have the same class time assigned to be grouped together. Some selections
              either do not have a requested class time assigned, or they are from different class times. Use the table
              filter to select students with the same class time before generating regular sessions.
            </div>
          );
        }
        return (
          <div className="mt-4">
            <div>
              {isScheduleStartInPast && <FormInputDate name="overrideStartPlainDate" label="Class date" form={form} />}
              <FormInputField name="groupSize" label="Group size" form={form} />
              <FormCheckbox name="autoCreateSessions" label="Auto-create sessions after creating classes" form={form} />
              <FormCheckbox
                name="prioritizeTeachersWithoutClasses"
                label="Prioritize teachers without classes"
                form={form}
                labelTooltip="Assign classes to eligible teachers without active classes before using the current grade-based priority."
              />
            </div>
          </div>
        );
      }}
    </FormModal>
  );
};
